/**
 * The text being written right now, for the tools outside it.
 *
 * Prose is written on the page, but most of its formatting is set from the
 * inspector, which has the room for it. So the editor in use says it is (see
 * `RichTextEditor.vue`), and the inspector's tools act on its selection.
 * `tick` moves with every change of that editor, its selection included, so
 * what the tools show as on (bold, centred, in red) follows the caret.
 *
 * One editor at a time: the section selected, or a text on a designed page
 * being typed into. Whichever was focused last is the one the tools act on.
 */

import type { Editor } from '@tiptap/vue-3'
import { readonly, ref, shallowRef } from 'vue'

const current = shallowRef<Editor | null>(null)
const tick = ref(0)

/** Says that `editor` is the text being written, until it is gone. Returns the way to stop. */
export function attachEditor(editor: Editor): () => void {
  const changed = (): void => {
    if (current.value === editor) tick.value += 1
  }
  const focused = (): void => {
    current.value = editor
    tick.value += 1
  }
  editor.on('transaction', changed)
  editor.on('focus', focused)
  current.value = editor
  tick.value += 1
  return () => {
    editor.off('transaction', changed)
    editor.off('focus', focused)
    if (current.value === editor) current.value = null
    tick.value += 1
  }
}

export function useTextFormatting() {
  return { editor: current, tick: readonly(tick) }
}
